/* eslint-disable camelcase */
import {
  CareerSiteFiltersOption,
  CareerSiteOpeningV2Response,
  CareerSiteV2Service,
} from 'api-clients/monolith';
import { ActiveFiltersV2, AppContext } from 'contexts/v2/app-context';
import { useCallback, useContext, useEffect, useRef } from 'react';
import { LocaleCode } from 'types/locale';

const DEFAULT_PAGE = 1;
const DEFAULT_SORT_BY = 'date_posted';
const DEFAULT_LOCATION = 'all';
const DEFAULT_QUERY = '';

const filterIncompatibleSelections = (
  selectedOptions: CareerSiteFiltersOption[],
  availableCounts: Array<{ id: string; count: number }>,
): CareerSiteFiltersOption[] => {
  if (!selectedOptions?.length || !availableCounts?.length) {
    return selectedOptions || [];
  }
  const availableIds = new Set(availableCounts.map(c => c.id));
  return selectedOptions.filter(opt => availableIds.has(opt.key));
};

const extractFilterCounts = (result: CareerSiteOpeningV2Response) => ({
  jobTypes: result.filter_counts.job_types ?? [],
  jobHours: result.filter_counts.job_hours ?? [],
  countries: result.filter_counts.countries ?? [],
  states: result.filter_counts.states ?? [],
  cities: result.filter_counts.cities ?? [],
  category: result.filter_counts.category ?? [],
  position: result.filter_counts.position ?? [],
  locationName: result.filter_counts.locationName ?? [],
});

const mapFilterArray = (arr?: { key: string }[]): string[] | undefined =>
  arr?.length ? arr.map(opt => opt.key) : undefined;

const mapFiltersToApiParams = (filters: ActiveFiltersV2) => ({
  category: mapFilterArray(filters.category),
  position: mapFilterArray(filters.position),
  jobType: mapFilterArray(filters.jobType),
  jobHours: mapFilterArray(filters.jobHours),
  country: mapFilterArray(filters.country),
  state: mapFilterArray(filters.state),
  city: mapFilterArray(filters.city),
  locationName: mapFilterArray(filters.locationName),
});

export function useJobs() {
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastSetLocationRef = useRef<string | null>(null);
  const skipFetchAfterAutoDeselectRef = useRef(false);
  const isFiltersDrawerOpenRef = useRef(false);
  const isMobileRef = useRef(false);

  const {
    accountSlug,
    brandId,
    location,
    setLocation,
    displayLocation,
    setDisplayLocation,
    replaceUrlWithParams,
    activeFiltersV2,
    setActiveFiltersV2,
    pendingFiltersV2,
    jobSearchQuery,
    setIsFetchingJobs,
    isMobile,
    isJobsFromCurrentLocation,
    page,
    currentLanguage,
    radius,
    sortBy,
    jobs,
    setJobs,
    paginationData,
    setPaginationData,
    currentCity,
    setCurrentCity,
    currentState,
    setCurrentState,
    currentCountry,
    setCurrentCountry,
    filterCounts,
    setFilterCounts,
    isFiltersDrawerOpen,
  } = useContext(AppContext);

  const autoDeselectIncompatibleFilters = useCallback(
    (
      newFilterCounts: {
        countries: Array<{ id: string; count: number }>;
        states: Array<{ id: string; count: number }>;
        cities: Array<{ id: string; count: number }>;
      },
      filtersToUse: typeof activeFiltersV2,
      setFilters: typeof setActiveFiltersV2,
    ) => {
      const newCountries = filterIncompatibleSelections(
        filtersToUse.country,
        newFilterCounts.countries,
      );
      const newStates = filterIncompatibleSelections(
        filtersToUse.state,
        newFilterCounts.states,
      );
      const newCities = filterIncompatibleSelections(
        filtersToUse.city,
        newFilterCounts.cities,
      );

      const countriesChanged =
        newCountries.length !== (filtersToUse.country?.length ?? 0);
      const statesChanged =
        newStates.length !== (filtersToUse.state?.length ?? 0);
      const citiesChanged =
        newCities.length !== (filtersToUse.city?.length ?? 0);

      if (countriesChanged || statesChanged || citiesChanged) {
        skipFetchAfterAutoDeselectRef.current = true;

        setFilters(prev => ({
          ...prev,
          country: newCountries,
          state: newStates,
          city: newCities,
        }));

        if (setFilters === setActiveFiltersV2 && accountSlug) {
          if (countriesChanged) {
            replaceUrlWithParams(
              accountSlug,
              brandId,
              'country',
              newCountries.map(c => c.key),
            );
          }
          if (statesChanged) {
            replaceUrlWithParams(
              accountSlug,
              brandId,
              'state',
              newStates.map(s => s.key),
            );
          }
          if (citiesChanged) {
            replaceUrlWithParams(
              accountSlug,
              brandId,
              'city',
              newCities.map(c => c.key),
            );
          }
        }
      }
    },
    [accountSlug, brandId, replaceUrlWithParams, setActiveFiltersV2],
  );

  const defaultLocale: LocaleCode = (currentLanguage as LocaleCode) ?? 'en-US';
  const hasLocationFilters =
    activeFiltersV2.city?.length ||
    activeFiltersV2.state?.length ||
    activeFiltersV2.country?.length;
  const hasSearchLocation =
    location.value && location.value !== 'current_location';

  isFiltersDrawerOpenRef.current = isFiltersDrawerOpen;
  isMobileRef.current = isMobile;

  const buildApiCall = useCallback(
    (filters: ActiveFiltersV2, countsOnly: boolean) => {
      const apiParams = {
        account_slug: accountSlug ?? '',
        brand_id: brandId,
        location_id:
          location.value !== 'all' ? (location.id ?? undefined) : undefined,
        is_jobs_from_current_location:
          location.value !== 'all' ? isJobsFromCurrentLocation : false,
      };

      const radiusValue = location.value !== 'all' ? radius?.key : undefined;
      const filterParams = mapFiltersToApiParams(filters);

      return CareerSiteV2Service.getInternalApiCareerSiteV2Openings(
        apiParams,
        page ?? DEFAULT_PAGE,
        radiusValue,
        (sortBy?.key ?? DEFAULT_SORT_BY) as
          | 'distance'
          | 'title'
          | 'date_posted',
        jobSearchQuery ?? DEFAULT_QUERY,
        location.value ?? DEFAULT_LOCATION,
        defaultLocale,
        filterParams.category,
        filterParams.position,
        filterParams.jobType,
        filterParams.jobHours,
        filterParams.country,
        filterParams.state,
        filterParams.city,
        filterParams.locationName,
        countsOnly,
      );
    },
    [
      accountSlug,
      brandId,
      location,
      isJobsFromCurrentLocation,
      radius,
      page,
      sortBy,
      jobSearchQuery,
      defaultLocale,
    ],
  );

  useEffect(() => {
    if (isFiltersDrawerOpenRef.current) return undefined;
    if (!accountSlug) return undefined;

    if (skipFetchAfterAutoDeselectRef.current) {
      skipFetchAfterAutoDeselectRef.current = false;
      return undefined;
    }

    if (lastSetLocationRef.current) {
      if (location.value === lastSetLocationRef.current) {
        lastSetLocationRef.current = null;
        return undefined;
      }
      if (location.value === 'current_location') return undefined;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    setIsFetchingJobs(true);

    buildApiCall(activeFiltersV2, false)
      .then((result: CareerSiteOpeningV2Response) => {
        if (abortController.signal.aborted) return;

        if (isMobileRef.current && page && page > 1) {
          setJobs(prev => [...prev, ...result.openings]);
        } else {
          setJobs(result.openings);
        }
        setPaginationData(result.pagination);

        const newFilterCounts = extractFilterCounts(result);
        setFilterCounts(newFilterCounts);

        if (result.current_city) setCurrentCity(result.current_city);
        if (result.current_state) setCurrentState(result.current_state);
        if (result.current_country) setCurrentCountry(result.current_country);

        autoDeselectIncompatibleFilters(
          {
            countries: newFilterCounts.countries,
            states: newFilterCounts.states,
            cities: newFilterCounts.cities,
          },
          activeFiltersV2,
          setActiveFiltersV2,
        );

        if (!hasLocationFilters && !hasSearchLocation && result.current_city) {
          const fullLocation = [
            result.current_city,
            result.current_state,
            result.current_country,
          ]
            .filter(Boolean)
            .join(', ');

          if (fullLocation !== displayLocation) {
            lastSetLocationRef.current = fullLocation;
            setDisplayLocation(fullLocation);
            setLocation({ id: null, value: fullLocation });
            replaceUrlWithParams?.(
              accountSlug,
              brandId,
              'location',
              fullLocation,
            );
          }
        }
      })
      .catch((error: Error) => {
        if (error.name === 'AbortError' || abortController.signal.aborted)
          return;
        setJobs([]);
        setPaginationData(undefined);
        setFilterCounts(null);
      })
      .finally(() => {
        if (abortControllerRef.current === abortController) {
          setIsFetchingJobs(false);
        }
      });

    return () => {
      abortController.abort();
    };
  }, [
    accountSlug,
    brandId,
    activeFiltersV2,
    location,
    jobSearchQuery,
    page,
    radius,
    sortBy,
    hasLocationFilters,
    hasSearchLocation,
    displayLocation,
    buildApiCall,
    setCurrentCity,
    setCurrentState,
    setCurrentCountry,
    setFilterCounts,
    setIsFetchingJobs,
    setJobs,
    setLocation,
    setPaginationData,
    setDisplayLocation,
    replaceUrlWithParams,
    autoDeselectIncompatibleFilters,
    setActiveFiltersV2,
  ]);

  useEffect(() => {
    if (!isFiltersDrawerOpen) return undefined;
    if (!accountSlug) return undefined;

    if (skipFetchAfterAutoDeselectRef.current) {
      skipFetchAfterAutoDeselectRef.current = false;
      return undefined;
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    buildApiCall(pendingFiltersV2, true)
      .then((result: CareerSiteOpeningV2Response) => {
        if (abortController.signal.aborted) return;

        setFilterCounts(extractFilterCounts(result));
      })
      .catch((error: Error) => {
        if (error.name === 'AbortError' || abortController.signal.aborted)
          return;
        setFilterCounts(null);
      });

    return () => {
      abortController.abort();
    };
  }, [
    isFiltersDrawerOpen,
    accountSlug,
    pendingFiltersV2,
    buildApiCall,
    setFilterCounts,
  ]);

  return {
    jobs,
    paginationData,
    isCurrentLocation: isJobsFromCurrentLocation,
    currentCity,
    currentState,
    currentCountry,
    filterCounts,
  };
}
